"use client"

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react"
import { SX_CONFIG, SYSTEM_SX_BET_MINIMUM_MARKETS } from "@/SX/constants"
import useChainProviderHook from "@/hooks/useChainProvider"
import { buildUrl } from "@/SX/api"
import { calculateOdds } from "@/SX/hooks/useSXMarketData"
import { calculateWeightedOdds } from "@/SX/helper"
import { useTicketAmmQuote } from "@/SX/hooks/useTicketAmmQuote"

// Add localStorage key for SX betslip with chain-specific storage
const SX_BETSLIP_STORAGE_KEY = "sx-betslip-tickets-by-chain"
const SX_BETSLIP_AMOUNT_KEY = "sx-betslip-amount-by-chain"

export enum SxBetslipDisableReason {
  ConditionState = "ConditionState",
  BetAmountGreaterThanMaxBet = "BetAmountGreaterThanMaxBet",
  BetAmountLowerThanMinBet = "BetAmountLowerThanMinBet",
  InsufficientBalance = "InsufficientBalance",
}

// SX ticket item type
export interface SxTicketItem {
  marketHash: string
  outcome: string
  percentageOdds: string
  outcomeOneName: string
  outcomeTwoName: string
  isMakerBettingOutcomeOne: boolean
  marketName: string
  game: any
  baseToken: string
  uniqueId?: string // Make optional for backward compatibility
  allOdds?: { odd: number; takerBetAmount: number; orderHash: string }[] // All odds for the ticket
  odds: any
}

// Placeholder for payment/collateral info
interface SxPayment {
  // Add SX payment/collateral fields if needed
}

export type DetailedSxBetslipContextValue = {
  betAmount: string
  odds: number
  totalOdds: number
  weightageOdds: number
  maxBet: number | undefined
  minBet: number | undefined
  states: Record<string, string>
  disableReason: SxBetslipDisableReason | undefined
  changeBetAmount: (value: string) => void
  isStatesFetching: boolean
  isOddsFetching: boolean
  isMaxBetFetching: boolean
  isBetAllowed: boolean
  ticket: SxTicketItem[]
  payout: number
  isFetching: boolean
  payment: SxPayment
  slippage: number
  setSlippage: (slippage: number) => void
  addToTicket: (item: SxTicketItem) => void
  removeFromTicket: (uniqueId: string) => void
  removeAll: () => void
  cleanupUnavailableMarkets: () => void
}

const DetailedSxBetslipContext = createContext<
  DetailedSxBetslipContextValue | undefined
>(undefined)

export const DetailedSxBetslipProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const { chainId } = useChainProviderHook()

  // Validate that the current chain is supported by SX
  const isSXChainSupported = useMemo(() => {
    return chainId && SX_CONFIG[chainId]
  }, [chainId])

  // Migration function to convert old localStorage format to new chain-specific format
  const migrateOldLocalStorage = (): {
    ticketsByChainId: Record<number, SxTicketItem[]>
    betAmountsByChainId: Record<number, string>
  } => {
    if (typeof window !== "undefined" && isSXChainSupported) {
      try {
        // Check for old format keys
        const oldTicketKey = "sx-betslip-ticket"
        const oldAmountKey = "sx-betslip-amount"

        const oldTickets = localStorage.getItem(oldTicketKey)
        const oldAmount = localStorage.getItem(oldAmountKey)

        if (oldTickets || oldAmount) {
          // Migrate old data to new format
          const ticketsByChainId: Record<number, SxTicketItem[]> = {}
          const betAmountsByChainId: Record<number, string> = {}

          if (oldTickets) {
            try {
              const parsedTickets = JSON.parse(oldTickets)
              if (Array.isArray(parsedTickets)) {
                // For now, assign old tickets to the first available SX chain
                // This is a reasonable fallback since SX only supports single bets
                const firstSxChainId = Object.keys(SX_CONFIG)[0]
                if (firstSxChainId) {
                  ticketsByChainId[parseInt(firstSxChainId)] = parsedTickets
                }
              }
            } catch (error) {
              console.error("Error parsing old SX tickets:", error)
            }
          }

          if (oldAmount) {
            try {
              const firstSxChainId = Object.keys(SX_CONFIG)[0]
              if (firstSxChainId) {
                betAmountsByChainId[parseInt(firstSxChainId)] = oldAmount
              }
            } catch (error) {
              console.error("Error parsing old SX amount:", error)
            }
          }

          // Clean up old keys
          localStorage.removeItem(oldTicketKey)
          localStorage.removeItem(oldAmountKey)

          console.log("Migrated SX betslip data to new chain-specific format")

          return { ticketsByChainId, betAmountsByChainId }
        }
      } catch (error) {
        console.error("Error during SX betslip migration:", error)
      }
    }

    return { ticketsByChainId: {}, betAmountsByChainId: {} }
  }

  // Initialize tickets by chain ID from localStorage if available
  const getInitialTicketsByChainId = (): Record<number, SxTicketItem[]> => {
    if (typeof window !== "undefined" && isSXChainSupported) {
      try {
        const stored = localStorage.getItem(SX_BETSLIP_STORAGE_KEY)
        if (stored) {
          return JSON.parse(stored)
        }

        // If no new format data, try to migrate old format
        const { ticketsByChainId } = migrateOldLocalStorage()
        return ticketsByChainId
      } catch (error) {
        console.error("Error loading SX betslip from localStorage:", error)
        return {}
      }
    }
    return {}
  }

  // Initialize bet amounts by chain ID from localStorage if available
  const getInitialBetAmountsByChainId = (): Record<number, string> => {
    if (typeof window !== "undefined" && isSXChainSupported) {
      try {
        const stored = localStorage.getItem(SX_BETSLIP_AMOUNT_KEY)
        if (stored) {
          return JSON.parse(stored)
        }

        // If no new format data, try to migrate old format
        const { betAmountsByChainId } = migrateOldLocalStorage()
        return betAmountsByChainId
      } catch (error) {
        console.error(
          "Error loading SX betslip amounts from localStorage:",
          error
        )
        return {}
      }
    }
    return {}
  }

  const [ticketsByChainId, setTicketsByChainId] = useState<
    Record<number, SxTicketItem[]>
  >(getInitialTicketsByChainId)
  const [betAmountsByChainId, setBetAmountsByChainId] = useState<
    Record<number, string>
  >(getInitialBetAmountsByChainId)
  const [odds, setOdds] = useState<number>(0)
  const [states, setStates] = useState<Record<string, string>>({})
  const [maxBet, setMaxBet] = useState<number | undefined>(undefined)
  const [slippage, setSlippage] = useState(isSXChainSupported ? 2 : 0)
  const [minBet, setMinBet] = useState<number | undefined>(
    isSXChainSupported ? SYSTEM_SX_BET_MINIMUM_MARKETS : undefined
  )

  // Update slippage when chain support changes
  useEffect(() => {
    if (isSXChainSupported) {
      setMinBet(SYSTEM_SX_BET_MINIMUM_MARKETS)
      setSlippage(2)
    } else {
      setSlippage(0)
      setMinBet(undefined)
    }
  }, [isSXChainSupported])

  // const [disableReason, setDisableReason] = useState<
  //   SxBetslipDisableReason | undefined
  // >()
  const [isStatesFetching, setIsStatesFetching] = useState(false)
  const [isOddsFetching, setIsOddsFetching] = useState(false)
  // Show loader only on initial odds fetch or when ticket changes
  const [hasLoadedOdds, setHasLoadedOdds] = useState(false)
  const [isMaxBetFetching, setIsMaxBetFetching] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [allOdds, setAllOdds] = useState<any[]>([])
  const [payout, setPayout] = useState(0)
  const [totalOdds, setTotalOdds] = useState(0)
  const [payment, setPayment] = useState<SxPayment>({})

  // Get current chain's ticket and bet amount
  const currentTicket = useMemo(() => {
    const ticket =
      isSXChainSupported && chainId ? ticketsByChainId[chainId] || [] : []
    return ticket
  }, [chainId, ticketsByChainId, isSXChainSupported])

  const currentBetAmount = useMemo(() => {
    const amount =
      isSXChainSupported && chainId ? betAmountsByChainId[chainId] || "" : ""
    return amount
  }, [chainId, betAmountsByChainId, isSXChainSupported])

  // Save tickets by chain ID to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== "undefined" && isSXChainSupported) {
      try {
        localStorage.setItem(
          SX_BETSLIP_STORAGE_KEY,
          JSON.stringify(ticketsByChainId)
        )
      } catch (error) {
        console.error("Error saving SX betslip to localStorage:", error)
      }
    }
  }, [ticketsByChainId, isSXChainSupported])

  // Save bet amounts by chain ID to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== "undefined" && isSXChainSupported) {
      try {
        localStorage.setItem(
          SX_BETSLIP_AMOUNT_KEY,
          JSON.stringify(betAmountsByChainId)
        )
      } catch (error) {
        console.error("Error saving SX betslip amounts to localStorage:", error)
      }
    }
  }, [betAmountsByChainId, isSXChainSupported])

  // Reset bet amount when there are no tickets for current chain
  useEffect(() => {
    if (
      currentTicket.length === 0 &&
      currentBetAmount !== "" &&
      chainId &&
      isSXChainSupported
    ) {
      setBetAmountsByChainId(prev => ({
        ...prev,
        [chainId]: "",
      }))
    }
  }, [currentTicket.length, currentBetAmount, chainId, isSXChainSupported])

  // Clean up odds and states when chain changes
  // useEffect(() => {
  //   // Clear odds and states when switching chains to avoid stale data
  //   setOdds(0)
  //   setStates({})
  //   setHasLoadedOdds(false)
  // }, [chainId, isSXChainSupported])

  // Clear odds and states when chain support changes
  useEffect(() => {
    if (!isSXChainSupported) {
      setOdds(0)
      setStates({})
      setHasLoadedOdds(false)
      setTotalOdds(0)
      setPayout(0)
      setMaxBet(undefined)
      setIsOddsFetching(false)
      setIsStatesFetching(false)
      setIsMaxBetFetching(false)
      setIsFetching(false)
      // setDisableReason(SxBetslipDisableReason.ConditionState)
      setPayment({})
      setSlippage(0)
    }
  }, [isSXChainSupported])

  // Placeholder for isBetAllowed logic
  // const isAmountBiggerThanMinBet = useMemo(() => {
  //   if (!isSXChainSupported) {
  //     return true
  //   }
  //   return Boolean(currentBetAmount) && typeof minBet !== "undefined"
  //     ? +currentBetAmount >= minBet
  //     : true
  // }, [isSXChainSupported, currentBetAmount, minBet])

  // const isAmountLowerThanMaxBet = useMemo(() => {
  //   if (!isSXChainSupported) {
  //     return true
  //   }
  //   return Boolean(currentBetAmount) && typeof maxBet !== "undefined"
  //     ? +currentBetAmount <= maxBet
  //     : true
  // }, [isSXChainSupported, currentBetAmount, maxBet])

  // const areAllItemsActive = useMemo(() => {
  //   if (!isSXChainSupported) {
  //     return false
  //   }
  //   return currentTicket.every(item => states[item.marketHash] === "active")
  // }, [isSXChainSupported, currentTicket, states])
  // const isBetAllowed = useMemo(() => {
  //   if (!isSXChainSupported) {
  //     return false
  //   }
  //   return (
  //     isAmountBiggerThanMinBet && isAmountLowerThanMaxBet && areAllItemsActive
  //   )
  // }, [
  //   isSXChainSupported,
  //   isAmountBiggerThanMinBet,
  //   isAmountLowerThanMaxBet,
  //   areAllItemsActive,
  // ])

  // // Placeholder for disableReason logic
  // // TODO
  // useEffect(() => {
  //   if (!isSXChainSupported) {
  //     setDisableReason(SxBetslipDisableReason.ConditionState)
  //   } else if (!areAllItemsActive) {
  //     // Check if any markets are unavailable (settled state)
  //     const hasUnavailableMarkets = currentTicket.some(
  //       item => states[item.marketHash] === "settled"
  //     )

  //     if (hasUnavailableMarkets) {
  //       setDisableReason(SxBetslipDisableReason.ConditionState)
  //     } else {
  //       setDisableReason(SxBetslipDisableReason.ConditionState)
  //     }
  //   } else if (!isAmountBiggerThanMinBet) {
  //     setDisableReason(SxBetslipDisableReason.BetAmountLowerThanMinBet)
  //   } else if (!isAmountLowerThanMaxBet) {
  //     setDisableReason(SxBetslipDisableReason.BetAmountGreaterThanMaxBet)
  //   } else {
  //     setDisableReason(undefined)
  //   }
  // }, [
  //   isAmountLowerThanMaxBet,
  //   isSXChainSupported,
  //   areAllItemsActive,
  //   isAmountBiggerThanMinBet,
  //   currentTicket,
  //   states,
  // ])

  const { isBetAllowed, disableReason } = useMemo(() => {
    // default fallback
    if (!isSXChainSupported) {
      return {
        isBetAllowed: false,
        disableReason: SxBetslipDisableReason.ConditionState,
      }
    }

    const hasUnavailableMarkets = currentTicket.some(
      item => states[item.marketHash] === "settled"
    )
    const allActive = currentTicket.every(
      item => states[item.marketHash] === "active"
    )

    if (!allActive || hasUnavailableMarkets) {
      return {
        isBetAllowed: false,
        disableReason: SxBetslipDisableReason.ConditionState,
      }
    }

    if (currentBetAmount != null && currentBetAmount !== "") {
      const numericAmount = +currentBetAmount

      if (typeof minBet !== "undefined" && numericAmount < minBet) {
        return {
          isBetAllowed: false,
          disableReason: SxBetslipDisableReason.BetAmountLowerThanMinBet,
        }
      }

      if (typeof maxBet !== "undefined" && numericAmount > maxBet) {
        return {
          isBetAllowed: false,
          disableReason: SxBetslipDisableReason.BetAmountGreaterThanMaxBet,
        }
      }
    }
    return { isBetAllowed: true, disableReason: undefined }
  }, [
    isSXChainSupported,
    currentTicket,
    states,
    currentBetAmount,
    minBet,
    maxBet,
  ])

  // Fetch odds and states for all ticket items
  // const fetchOddsAndStates = async () => {
  //   // Ensure we have a valid chain ID and it's supported by SX before proceeding
  //   if (!chainId || !isSXChainSupported) {
  //     console.warn(
  //       `SX Betslip: Chain ${chainId} is not supported by SX or no chain ID available`
  //     )
  //     return
  //   }

  //   // Only show loader if we haven't loaded odds yet for the current ticket
  //   if (!hasLoadedOdds) {
  //     setIsOddsFetching(true)
  //     setIsStatesFetching(true)
  //   }
  //   const newOdds: Record<string, number> = {}
  //   const newStates: Record<string, string> = {}

  //   try {
  //     // Collect market hashes from current ticket
  //     const marketHashes = currentTicket
  //       .map(item => item.marketHash)
  //       .filter(Boolean)

  //     if (marketHashes.length > 0) {
  //       // Import both APIs to get the same data as the market
  //       const { fetchBestMarkets, fetchOrders } = await import("@/SX/api")

  //       // Fetch best odds from SX API for these markets with chain ID
  //       const response = await fetchBestMarkets(marketHashes, chainId)
  //       const bestOdds = response?.data?.bestOdds || []

  //       // Also fetch orders for fallback and debugging
  //       const ordersResponse = await fetchOrders(marketHashes, chainId)
  //       const orders = ordersResponse?.data || []

  //       // Map best odds per ticket item and selected outcome (original logic)
  //       currentTicket.forEach(item => {
  //         const bestForMarket = bestOdds.find(
  //           (o: any) => o.marketHash === item.marketHash
  //         )

  //         // If market is not found in current market data, disable it
  //         if (!bestForMarket) {
  //           // Check if we have any orders for this market as fallback
  //           const marketOrders = orders.filter(
  //             (order: any) => order.marketHash === item.marketHash
  //           )

  //           if (marketOrders.length === 0) {
  //             // Market is completely unavailable - disable it
  //             newOdds[item.marketHash] = 0
  //             newStates[item.marketHash] = "settled"
  //             return
  //           }
  //         }

  //         // Fallback: compute from stored percentage if API not available
  //         if (!bestForMarket) {
  //           const percentageOdds = parseFloat(item.percentageOdds)
  //           const offeredOdds = 10 ** 20 / (10 ** 20 - percentageOdds)
  //           newOdds[item.marketHash] = offeredOdds
  //           newStates[item.marketHash] = "active"
  //           return
  //         }

  //         const selectedOutcome = item.isMakerBettingOutcomeOne
  //           ? bestForMarket.outcomeOne
  //           : bestForMarket.outcomeTwo

  //         const percentage = Number(selectedOutcome?.percentageOdds || 0)
  //         const offeredOdds =
  //           percentage > 0 && percentage < 10 ** 20
  //             ? 10 ** 20 / (10 ** 20 - percentage)
  //             : 0

  //         newOdds[item.marketHash] = offeredOdds
  //         // TODO: Enhance with real state when fixture status is wired
  //         newStates[item.marketHash] = "active"
  //       })
  //     } else {
  //       // No markets selected; clear odds/states
  //       currentTicket.forEach(item => {
  //         newOdds[item.marketHash] = 0
  //         newStates[item.marketHash] = "settled"
  //       })
  //     }
  //   } catch (error) {
  //     // On error, gracefully fallback to existing ticket odds
  //     currentTicket.forEach(item => {
  //       const percentageOdds = parseFloat(item.percentageOdds)
  //       const offeredOdds = parseFloat(
  //         (10 ** 20 / (10 ** 20 - percentageOdds)).toFixed(2)
  //       )
  //       newOdds[item.marketHash] = offeredOdds
  //       newStates[item.marketHash] = "active"
  //     })
  //     // eslint-disable-next-line no-console
  //     console.error(
  //       `SX Betslip: Error fetching odds for chain ${chainId}:`,
  //       error
  //     )
  //   } finally {
  //     setOdds(newOdds)
  //     setStates(newStates)
  //     setHasLoadedOdds(true)
  //     setIsOddsFetching(false)
  //     setIsStatesFetching(false)
  //   }
  // }

  // useEffect(() => {
  //   if (currentTicket.length > 0 && isSXChainSupported) {
  //     // fetchOddsAndStates()
  //   }
  // }, [currentTicket, isSXChainSupported])

  // Reset loader flag when ticket changes (e.g., new market selected)
  // useEffect(() => {
  //   if (currentTicket.length === 0 || !isSXChainSupported) {
  //     setHasLoadedOdds(false)
  //   } else {
  //     setHasLoadedOdds(false)
  //   }
  // }, [
  //   currentTicket
  //     .map(t => `${t.marketHash}:${t.isMakerBettingOutcomeOne}`)
  //     .join("|"),
  //   isSXChainSupported,
  // ])

  // // Periodically refresh SX odds to match market refresh rate
  // useEffect(() => {
  //   if (currentTicket.length === 0 || !isSXChainSupported) {
  //     return
  //   }

  //   // Refresh every 10 seconds to match market refresh rate for better synchronization
  //   const intervalId = window.setInterval(() => {
  //     fetchOddsAndStates()
  //   }, 10_000)

  //   return () => {
  //     window.clearInterval(intervalId)
  //   }
  // }, [
  //   currentTicket
  //     .map(t => `${t.marketHash}:${t.isMakerBettingOutcomeOne}`)
  //     .join("|"),
  //   isSXChainSupported,
  // ])

  useEffect(() => {
    if (currentTicket.length && isSXChainSupported) {
      setTotalOdds(odds)
    } else {
      setTotalOdds(0)
    }
  }, [odds, currentTicket, isSXChainSupported])

  // TODO

  const weightageOdds = useMemo(() => {
    return calculateWeightedOdds(Number(currentBetAmount), allOdds)
  }, [currentBetAmount, currentTicket, odds, isSXChainSupported])

  useEffect(() => {
    // Update odds in the current ticket when weightageOdds changes
    // Only update if currentBetAmount is not 0, null, or empty string
    if (
      currentTicket.length > 0 &&
      isSXChainSupported &&
      currentBetAmount &&
      currentBetAmount.trim() !== "" &&
      Number(currentBetAmount) > 0
    ) {
      setTicketsByChainId(prev => ({
        ...prev,
        [chainId]: prev[chainId].map(ticket => ({
          ...ticket,
          odds: weightageOdds,
        })),
      }))
    }

    if (!weightageOdds && odds !== 0) {
      setTicketsByChainId(prev => ({
        ...prev,
        [chainId]: prev[chainId]
          ? prev[chainId].map(ticket => ({
              ...ticket,
              odds: odds,
            }))
          : [],
      }))
    }
  }, [weightageOdds, chainId, isSXChainSupported, currentBetAmount, odds])

  // Payout calculation using weighted odds (without deducting fees for display)
  useEffect(() => {
    if (currentBetAmount && weightageOdds > 0 && isSXChainSupported) {
      const SX_FEES = SX_CONFIG[chainId]?.NETWORK_FEES || 3
      const feesAmount =
        (Number(currentBetAmount) * weightageOdds - Number(currentBetAmount)) *
        (SX_FEES / 100)
      const payout = Number(currentBetAmount) * weightageOdds - feesAmount
      setPayout(payout)
    } else {
      setPayout(0)
    }
  }, [currentBetAmount, weightageOdds, chainId, isSXChainSupported])

  // Use React Query hook for fetching ticket AMM quote
  const { data: ticketQuoteData, isLoading: isQuoteLoading } =
    useTicketAmmQuote({
      currentTicket,
      enabled: isSXChainSupported && currentTicket.length > 0,
    })

  useEffect(() => {
    if (ticketQuoteData) {
      const state = ticketQuoteData.allOdds.length > 0 ? "active" : "settled"
      setStates(
        currentTicket.reduce<Record<string, string>>((acc, item) => {
          acc[item.marketHash] = state
          return acc
        }, {})
      )

      setMaxBet(ticketQuoteData.liquidity || 0)
      setAllOdds(ticketQuoteData.allOdds || [])
    }
  }, [ticketQuoteData])

  // Update fetching state based on React Query loading state
  useEffect(() => {
    setIsFetching(isQuoteLoading)
  }, [isQuoteLoading])

  const addToTicket = (item: SxTicketItem) => {
    if (!chainId || !isSXChainSupported) {
      console.warn(
        `SX Betslip: Cannot add ticket - chain ${chainId} not supported`
      )
      return
    }

    setOdds(item.odds)
    // For SX, we only allow single bets, so replace the entire ticket for the current chain
    setTicketsByChainId(prev => ({
      ...prev,
      [chainId]: [item],
    }))

    setStates(prev => ({ ...prev, [item.marketHash]: "active" }))
  }

  const removeFromTicket = (uniqueId: string) => {
    if (!chainId || !isSXChainSupported) {
      console.warn(
        `SX Betslip: Cannot remove ticket - chain ${chainId} not supported`
      )
      return
    }

    setTicketsByChainId(prev => ({
      ...prev,
      [chainId]: prev[chainId]?.filter(i => i.uniqueId !== uniqueId) || [],
    }))
  }

  const removeAll = () => {
    if (!chainId || !isSXChainSupported) {
      console.warn(
        `SX Betslip: Cannot remove tickets - chain ${chainId} not supported`
      )
      return
    }

    setTicketsByChainId(prev => ({
      ...prev,
      [chainId]: [],
    }))
  }

  const changeBetAmount = (value: string) => {
    if (!chainId || !isSXChainSupported) {
      console.warn(
        `SX Betslip: Cannot change bet amount - chain ${chainId} not supported`
      )
      return
    }

    setBetAmountsByChainId(prev => ({
      ...prev,
      [chainId]: value,
    }))
  }

  // Clean up unavailable markets from betslip
  const cleanupUnavailableMarkets = () => {
    if (!chainId || !isSXChainSupported || currentTicket.length === 0) {
      return
    }

    // Find markets that are marked as settled (unavailable)
    const unavailableMarkets = currentTicket.filter(
      item => states[item.marketHash] === "settled"
    )

    if (unavailableMarkets.length > 0) {
      // Remove unavailable markets from the betslip
      setTicketsByChainId(prev => ({
        ...prev,
        [chainId]:
          prev[chainId]?.filter(
            item =>
              !unavailableMarkets.some(
                unavailable => unavailable.marketHash === item.marketHash
              )
          ) || [],
      }))
    }
  }

  // Periodically clean up unavailable markets
  useEffect(() => {
    if (currentTicket.length === 0 || !isSXChainSupported) {
      return
    }

    // Clean up every 30 seconds
    const intervalId = window.setInterval(() => {
      cleanupUnavailableMarkets()
    }, 30_000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [currentTicket, isSXChainSupported])

  const value = useMemo(
    () => ({
      betAmount: currentBetAmount,
      odds,
      totalOdds,
      weightageOdds,
      maxBet,
      minBet,
      states,
      disableReason,
      changeBetAmount,
      isStatesFetching,
      isOddsFetching,
      isMaxBetFetching,
      isBetAllowed,
      ticket: currentTicket,
      payout,
      isFetching,
      payment,
      slippage,
      setSlippage,
      addToTicket,
      removeFromTicket,
      removeAll,
      cleanupUnavailableMarkets, // Add cleanup function
    }),
    [
      currentBetAmount,
      odds,
      totalOdds,
      weightageOdds,
      maxBet,
      minBet,
      states,
      disableReason,
      isStatesFetching,
      isOddsFetching,
      isMaxBetFetching,
      isBetAllowed,
      currentTicket,
      payout,
      isFetching,
      payment,
      slippage,
      cleanupUnavailableMarkets, // Add cleanup function to dependencies
    ]
  )

  return (
    <DetailedSxBetslipContext.Provider value={value}>
      {children}
    </DetailedSxBetslipContext.Provider>
  )
}

export const useDetailedSxBetslip = () => {
  const context = useContext(DetailedSxBetslipContext)
  if (!context) {
    throw new Error(
      "useDetailedSxBetslip must be used within DetailedSxBetslipProvider"
    )
  }
  return context
}
